// @flow
import {evaluationIsVariant} from '@homeaway/react-experiment';
import {type IntlShape} from 'react-intl';
import type {State as AbacusState} from '../../../reducers/abacus';
import {type Breakpoint, media} from '../../../utils/breakpoints';
import DayAvailability from '../../DayAvailability/DayAvailability';

type System = {
    fullLocale: string,
    hostname: string,
    siteName: string
};

type SearchValues = {
    q: string,
    filter: string[],
    arrival: string,
    departure: string,
    adultsCount: number,
    childrenCount: number,
    petIncluded: boolean,
    pinnedPropertyListing: string | null,
    filterSelected: boolean
};

function getOptions(system: System, abacus: AbacusState) {
    const {fullLocale, hostname, siteName} = system;

    const {HA_SERP_SEARCHFORM_v2: searchFormComponentV2Experiment} = abacus;

    const systemOptions = {
        site: siteName,
        baseUrl: hostname,
        pageType: 'serp',
        locale: fullLocale
    };

    const formOptions = {
        autoProgress: evaluationIsVariant(searchFormComponentV2Experiment, 1)
            ? 'standard'
            : 'none',
        autoSubmit: false,
        layout: 'standard',
        align: 'left',
        showSubmitDetailsButtonField: false
    };

    const destinationOptions = {
        autoProgress: evaluationIsVariant(searchFormComponentV2Experiment, 1)
            ? 'smart'
            : 'none'
    };

    let datesOptions = {
        allowErrorHighlighting: true,
        autoProgress: 'none',
        dateFormatOptions: {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        },
        dayTemplateComponent: DayAvailability
    };

    const guestsOptions = {
        actionLocation: 'search',
        adultsMin: 1
    };

    return {
        systemOptions,
        formOptions,
        destinationOptions,
        datesOptions,
        guestsOptions
    };
}

function getSettings(breakpoint: Breakpoint) {
    const mediaSettings = {
        isMobile: !media(breakpoint, {minWidth: 'SM_MIN'})
    };

    return {
        mediaSettings
    };
}

function getStores(abacus: AbacusState) {
    const abacusStore = {
        experimentData: abacus,
        experimentNames: []
    };

    return {
        abacusStore
    };
}

function getTranslations(values: SearchValues, intl: IntlShape) {
    const {formatMessage} = intl;

    const {adultsCount = 0, childrenCount = 0, petIncluded} = values;

    const adultsLabel = formatMessage(
        {
            id: 'guestsPickerAdultsLabel',
            defaultMessage:
                '{adultsCount, number} {adultsCount, plural, one {Adult} other {Adults}}'
        },
        {adultsCount}
    );

    const childrenLabel = formatMessage(
        {
            id: 'guestsPickerChildrenLabel',
            defaultMessage:
                '{childrenCount, number} {childrenCount, plural, one {Child} other {Children}}'
        },
        {childrenCount}
    );

    const guestLabel = () => {
        const sum = adultsCount + childrenCount;
        if (sum > 0) {
            return formatMessage(
                {
                    id: 'condensedSearchBarGuests',
                    defaultMessage:
                        '{sum, number} {sum, plural, one {guest} other {guests}}{pets, select, yes {, pets} no {}}'
                },
                {sum, pets: petIncluded ? 'yes' : 'no'}
            );
        }
        return '';
    };

    const petMessage = formatMessage({
        id: 'guestsPickerPetsMessage',
        defaultMessage: 'Only properties that allow pets will be shown'
    });

    const petLabel = formatMessage({
        id: 'guestsPickerPetLabel',
        defaultMessage: 'Pets'
    });

    const petYesLabel = formatMessage({
        id: 'petYesLabel',
        defaultMessage: 'Yes'
    });

    const petNoLabel = formatMessage({
        id: 'petNoLabel',
        defaultMessage: 'No'
    });

    const label = formatMessage({
        id: 'guestsPickerLabel',
        defaultMessage: 'Guests'
    });

    const continueButtonLabel = formatMessage({
        id: 'datePickerContinueLabel',
        defaultMessage: 'Continue'
    });

    const applyButtonLabel = formatMessage({
        id: 'formModalApplyLabel',
        defaultMessage: 'Apply'
    });

    const clearButtonLabel = formatMessage({
        id: 'datePickerClearLabel',
        defaultMessage: 'Clear'
    });

    const closeButtonLabel = formatMessage({
        id: 'datePickerCloseLabel',
        defaultMessage: 'Close'
    });

    const modalTitleStartDateMessage = formatMessage({
        id: 'selectCheckIn',
        defaultMessage: 'Select a check-in date'
    });

    const modalTitleEndDateMessage = formatMessage({
        id: 'selectCheckOut',
        defaultMessage: 'Select a checkout date'
    });

    const startDateLabel = formatMessage({
        id: 'checkIn',
        defaultMessage: 'Check In'
    });

    const endDateLabel = formatMessage({
        id: 'checkOut',
        defaultMessage: 'Check Out'
    });

    const datesTranslations = {
        modalTitleStartDateMessage,
        startDateLabel,
        endDateLabel,
        modalTitleEndDateMessage,
        continueButtonLabel,
        clearButtonLabel,
        closeButtonLabel
    };

    const guestsTranslations = {
        adultsLabel,
        childrenLabel,
        guestLabel: guestLabel(),
        label,
        modalTitle: label,
        petLabel,
        petNoLabel,
        petYesLabel,
        petMessage,
        applyButtonLabel,
        continueButtonLabel
    };

    return {
        datesTranslations,
        guestsTranslations
    };
}

export {getOptions, getSettings, getStores, getTranslations};
